import type { Locale } from "@/lib/i18n/translations";
/**
 * Author profiles for guide articles: Schema.org Person markup in the article
 * JSON-LD, the author bio box and the inline article byline. Entries are
 * referenced by `@id` in the schema graph so author and organization are linked.
 *
 * Bios must not contain numeric claims ("since 2022", "2000+ couples"): such
 * claims would have to be verifiable (UWG).
 */

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL ?? "https://wedding-denmark.com";

export type AuthorId = "alexander" | "thi-yen" | "team";
export type AuthorGender = "m" | "f" | "team";

export type AuthorLocale = import("@/lib/i18n/translations").Locale;

export type AuthorProfile = {
  id: AuthorId;
  name: string;
  /** German default used for Schema.org and server rendering. */
  jobTitle: string;
  /** German default used for Schema.org and server rendering. */
  bio: string;
  /** Path relative to /public; made absolute with SITE_URL. */
  imagePath: string;
  sameAs: readonly string[];
  knowsLanguage: readonly string[];
  gender: AuthorGender;
  /** Localized variants of jobTitle and bio. */
  translations?: {
    en?: { jobTitle: string; bio: string };
    da?: { jobTitle: string; bio: string };
    ar?: { jobTitle: string; bio: string };
    uk?: { jobTitle: string; bio: string };
    ru?: { jobTitle: string; bio: string };
    vi?: { jobTitle: string; bio: string };
    es?: { jobTitle: string; bio: string };
  };
};

export const AUTHORS: Record<AuthorId, AuthorProfile> = {
  alexander: {
    id: "alexander",
    name: "Alexander Koltman",
    jobTitle: "Mitgründer · Ansprechpartner für AFL und Behörden",
    bio: "Alexander ist den dänischen Weg selbst gegangen und hilft heute anderen Paaren dabei. Als Informatiker (M.Sc.) hat er die sichere, DSGVO-konforme Plattform selbst gebaut. Er liest jeden Antrag persönlich vor der Einreichung und spricht Deutsch, Englisch und Russisch.",
    imagePath: "/images/team-alexander.jpg",
    sameAs: ["https://www.instagram.com/danish_wedding/"],
    knowsLanguage: ["de", "en", "ru"],
    gender: "m",
    translations: {
      en: {
        jobTitle: "Co-founder · Contact for AFL and authorities",
        bio: "Alexander walked the Danish route himself and now helps other couples do the same. As a computer scientist (M.Sc.) he built the secure, GDPR-compliant platform himself. He personally reads every application before submission and speaks German, English and Russian.",
      },
      da: {
        jobTitle: "Medstifter · Kontaktperson for AFL og myndigheder",
        bio: "Alexander gik selv den danske vej og hjælper i dag andre par med det samme. Som datalog (M.Sc.) har han selv bygget den sikre, GDPR-konforme platform. Han læser personligt hver ansøgning før indsendelse og taler tysk, engelsk og russisk.",
      },
      ar: {
        jobTitle: "شريك مؤسس · جهة الاتصال لشؤون AFL والسلطات",
        bio: "سلك Alexander الطريق الدنماركي بنفسه، ويساعد اليوم أزواجًا آخرين على سلوكه. وبصفته متخصصًا في علوم الحاسوب (ماجستير)، بنى بنفسه المنصة الآمنة المتوافقة مع اللائحة العامة لحماية البيانات (GDPR). يقرأ كل طلب شخصيًا قبل تقديمه، ويتحدث الألمانية والإنجليزية والروسية.",
      },
      uk: {
        jobTitle: "Співзасновник · контактна особа з питань AFL і державних органів",
        bio: "Alexander сам пройшов данський шлях і тепер допомагає іншим парам зробити те саме. Як інформатик (магістр наук) він сам створив захищену платформу, що відповідає вимогам GDPR. Він особисто перевіряє кожну заяву перед поданням і говорить німецькою, англійською та російською.",
      },
      ru: {
        jobTitle: "Сооснователь · контакт по вопросам AFL и ведомств",
        bio: "Alexander сам прошёл датский путь и теперь помогает другим парам пройти его. Как специалист по информатике (магистр наук) он сам создал защищённую платформу, соответствующую требованиям GDPR. Он лично проверяет каждое заявление перед подачей и говорит на немецком, английском и русском языках.",
      },
      vi: {
        jobTitle: "Đồng sáng lập · Phụ trách AFL và làm việc với cơ quan chức năng",
        bio: "Alexander đã tự mình đi con đường kết hôn tại Đan Mạch và nay giúp các cặp đôi khác làm điều tương tự. Là chuyên gia tin học (thạc sĩ), anh tự xây dựng nền tảng an toàn, tuân thủ GDPR. Anh trực tiếp đọc từng hồ sơ trước khi nộp và nói tiếng Đức, tiếng Anh và tiếng Nga.",
      },
      es: {
        jobTitle: "Cofundador · Contacto para la AFL y las autoridades",
        bio: "Alexander recorrió él mismo el camino danés y hoy ayuda a otras parejas a hacerlo. Como informático (M.Sc.), construyó él mismo la plataforma segura y conforme al RGPD. Lee personalmente cada solicitud antes de presentarla y habla alemán, inglés y ruso.",
      },
    },
  },
  "thi-yen": {
    id: "thi-yen",
    name: "Thi Yen Le",
    jobTitle: "Mitgründerin · Doku-Vorprüfung und Apostille-Logistik",
    bio: "Thi Yen koordiniert Übersetzer und Konsulate für binationale Paare. Sie prüft jede Scan-Datei vor Einreichung und spricht Deutsch, Englisch und Vietnamesisch.",
    imagePath: "/images/team-thi-yen.jpg",
    sameAs: ["https://www.instagram.com/danish_wedding/"],
    knowsLanguage: ["de", "en", "vi"],
    gender: "f",
    translations: {
      en: {
        jobTitle: "Co-founder · Document pre-check and apostille logistics",
        bio: "Thi Yen coordinates translators and consulates for binational couples. She reviews every scan before submission and speaks German, English and Vietnamese.",
      },
      da: {
        jobTitle: "Medstifter · Dokumentforkontrol og apostillelogistik",
        bio: "Thi Yen koordinerer oversættere og konsulater for binationale par. Hun gennemgår hver scanning før indsendelse og taler tysk, engelsk og vietnamesisk.",
      },
      ar: {
        jobTitle: "شريكة مؤسسة · التدقيق المسبق للوثائق ولوجستيات الأبوستيل",
        bio: "تنسّق Thi Yen العمل مع المترجمين والقنصليات للأزواج من جنسيتين مختلفتين. تراجع كل ملف ممسوح ضوئيًا قبل تقديمه، وتتحدث الألمانية والإنجليزية والفيتنامية.",
      },
      uk: {
        jobTitle: "Співзасновниця · попередня перевірка документів і логістика апостилю",
        bio: "Thi Yen координує роботу перекладачів і консульств для пар різних громадянств. Вона перевіряє кожен скан перед поданням і говорить німецькою, англійською та в’єтнамською.",
      },
      ru: {
        jobTitle: "Сооснователь · предварительная проверка документов и логистика апостиля",
        bio: "Thi Yen координирует работу переводчиков и консульств для пар с разным гражданством. Она проверяет каждый скан перед подачей и говорит на немецком, английском и вьетнамском языках.",
      },
      vi: {
        jobTitle: "Đồng sáng lập · Kiểm tra giấy tờ trước và điều phối Apostille",
        bio: "Thi Yen điều phối phiên dịch viên và lãnh sự quán cho các cặp đôi khác quốc tịch. Chị kiểm tra từng bản scan trước khi nộp và nói tiếng Đức, tiếng Anh và tiếng Việt.",
      },
      es: {
        jobTitle: "Cofundadora · Revisión previa de documentos y logística de la apostilla",
        bio: "Thi Yen coordina a traductores y consulados para parejas binacionales. Revisa cada archivo escaneado antes de presentarlo y habla alemán, inglés y vietnamita.",
      },
    },
  },
  team: {
    id: "team",
    name: "Danish Wedding Team",
    jobTitle: "Redaktion · Behörden, Dokumente, Ablauf",
    bio: "Dieser Beitrag wurde gemeinsam vom Danish Wedding Team verfasst. Wir kennen die dänische Heiratsbürokratie aus eigener Erfahrung und begleiten andere Paare auf demselben Weg.",
    // The flag mark: one founder's face labelled "Team" read as a single person.
    imagePath: "/images/team-danish-wedding.png",
    sameAs: [],
    knowsLanguage: ["de", "en", "da"],
    gender: "team",
    translations: {
      en: {
        jobTitle: "Editorial team · authorities, documents, process",
        bio: "This article was written together by the Danish Wedding team. We know the Danish marriage bureaucracy from our own experience and guide other couples on the same path.",
      },
      da: {
        jobTitle: "Redaktion · myndigheder, dokumenter, forløb",
        bio: "Denne artikel er skrevet i fællesskab af Danish Wedding-teamet. Vi kender det danske vielsesbureaukrati af egen erfaring og vejleder andre par på samme vej.",
      },
      ar: {
        jobTitle: "فريق التحرير · السلطات، الوثائق، الإجراءات",
        bio: "كتب فريق Danish Wedding هذا المقال معًا. نعرف البيروقراطية الدنماركية المتعلقة بالزواج من تجربتنا الشخصية، ونرافق أزواجًا آخرين على الطريق نفسه.",
      },
      uk: {
        jobTitle: "Редакція · органи влади, документи, процедура",
        bio: "Цю статтю спільно написала команда Danish Wedding. Ми знаємо данську бюрократію довкола шлюбу з власного досвіду та супроводжуємо інші пари тим самим шляхом.",
      },
      ru: {
        jobTitle: "Редакция · ведомства, документы, процедура",
        bio: "Эту статью совместно написала команда Danish Wedding. Мы знаем датскую бюрократию вокруг брака по собственному опыту и сопровождаем другие пары на том же пути.",
      },
      vi: {
        jobTitle: "Ban biên tập · Cơ quan chức năng, giấy tờ, thủ tục",
        bio: "Bài viết này do đội ngũ Danish Wedding cùng biên soạn. Chúng tôi hiểu thủ tục kết hôn tại Đan Mạch từ chính trải nghiệm của mình và đồng hành cùng các cặp đôi khác trên cùng con đường.",
      },
      es: {
        jobTitle: "Redacción · autoridades, documentos, proceso",
        bio: "Este artículo fue redactado en conjunto por el equipo de Danish Wedding. Conocemos la burocracia matrimonial danesa por experiencia propia y acompañamos a otras parejas en el mismo camino.",
      },
    },
  },
};

/** Returns jobTitle and bio for the locale, falling back to German. */
export function getAuthorLocalized(authorId: AuthorId, locale: AuthorLocale = "de") {
  const a = AUTHORS[authorId];
  if (locale === "de" || !a.translations || !a.translations[locale]) {
    return { jobTitle: a.jobTitle, bio: a.bio };
  }
  const tr = a.translations[locale];
  return tr ? { jobTitle: tr.jobTitle, bio: tr.bio } : { jobTitle: a.jobTitle, bio: a.bio };
}

/**
 * Builds the Schema.org Person object for the Article.author slot, with job
 * title and description in the article's language (German by default).
 */
export function authorPersonSchema(authorId: AuthorId, locale: AuthorLocale = "de") {
  const a = AUTHORS[authorId];
  const { jobTitle, bio } = getAuthorLocalized(authorId, locale);
  return {
    "@type": "Person" as const,
    "@id": `${SITE_URL}/#author-${a.id}`,
    name: a.name,
    jobTitle,
    description: bio,
    image: `${SITE_URL}${a.imagePath}`,
    knowsLanguage: a.knowsLanguage,
    worksFor: { "@id": `${SITE_URL}/#organization` },
    ...(a.sameAs.length > 0 ? { sameAs: a.sameAs } : {}),
  };
}

/** Localized heading for the author bio box. */
export function aboutAuthorLabel(authorId: AuthorId, locale: Locale = "de"): string {
  const a = AUTHORS[authorId];
  if (locale === "en") {
    if (a.gender === "team") return "About the team";
    return "About the author";
  }
  if (locale === "da") {
    if (a.gender === "team") return "Om holdet";
    return "Om forfatteren";
  }
  if (locale === "ar") {
    if (a.gender === "team") return "عن الفريق";
    if (a.gender === "f") return "عن الكاتبة";
    return "عن الكاتب";
  }
  if (locale === "uk") {
    if (a.gender === "team") return "Про команду";
    if (a.gender === "f") return "Про авторку";
    return "Про автора";
  }
  if (locale === "ru") {
    if (a.gender === "team") return "О команде";
    return "Об авторе";
  }
  if (locale === "vi") {
    if (a.gender === "team") return "Về đội ngũ";
    return "Về tác giả";
  }
  if (locale === "es") {
    if (a.gender === "team") return "Sobre el equipo";
    if (a.gender === "f") return "Sobre la autora";
    return "Sobre el autor";
  }
  if (a.gender === "team") return "Über das Team";
  if (a.gender === "f") return "Über die Autorin";
  return "Über den Autor";
}
